import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../axiosConfig';
import { getStoreUrl } from '../../config/platformConfig';

export const createOrUpdateStore = createAsyncThunk(
  'store/createOrUpdate',
  async ({ payload, axiosInstance }) => {
    const res = await axiosInstance.post('/stores', payload);
    return res.data;
  }
);

export const fetchStoreBySlug = createAsyncThunk(
  'store/fetchBySlug',
  async (slug, { rejectWithValue }) => {
    try {
      console.log('fetchStoreBySlug: Fetching store with slug:', slug);
      const res = await axios.get(`/stores/${slug}`, { withCredentials: true });
      console.log('fetchStoreBySlug: API response:', res.data);
      return res.data; // { store, products }
    } catch (error) {
      console.log('fetchStoreBySlug: API error:', error.response?.status, error.message);
      // Handle slug redirect (old slug → new slug)
      const data = error.response?.data;
      if (data?.redirect && data?.newSlug) {
        console.log('fetchStoreBySlug: Slug redirect detected, redirecting to:', data.newSlug);
        window.location.href = getStoreUrl(data.newSlug);
        // Return a pending-like state to prevent "not found" flash
        return rejectWithValue({ redirecting: true });
      }
      return rejectWithValue(data || error.message);
    }
  }
);

export const fetchStoreByDomain = createAsyncThunk(
  'store/fetchByDomain',
  async (_, { rejectWithValue }) => {
    try {
      const hostname = window.location.hostname;
      console.log('fetchStoreByDomain: Fetching store for domain:', hostname);
      const res = await axios.get(`/stores/current`, { withCredentials: true });
      console.log('fetchStoreByDomain: API response:', res.data);
      return res.data.data; // { store, products, resolvedBy }
    } catch (error) {
      console.log('fetchStoreByDomain: API error:', error.response?.status, error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchMyStore = createAsyncThunk(
  'store/fetchMyStore',
  async (axiosInstance, { rejectWithValue }) => {
    try {
      console.log('fetchMyStore: Making API call to /merchants/stores/me');
      const axiosToUse = axiosInstance || axios;
      console.log('fetchMyStore: Using axios instance with baseURL:', axiosToUse.defaults.baseURL);
      const res = await axiosToUse.get('/merchants/stores/me');
      console.log('fetchMyStore: API response:', res.data);
      console.log('fetchMyStore: Response status:', res.status);
      return res.data;
    } catch (e) {
      const status = e?.response?.status;
      console.log('fetchMyStore: API error:', status, e.message);
      console.log('fetchMyStore: Error response:', e.response?.data);
      if (status === 404) {
        // No store yet is not an error for the dashboard
        return rejectWithValue({ status: 404 });
      }
      if (status === 401 || status === 403) {
        // Token is invalid, will be handled by auth service
        return rejectWithValue({ status: 401, message: 'Invalid token' });
      }
      return rejectWithValue({ status: status || 500, message: e.message });
    }
  }
);

export const updateStore = createAsyncThunk(
  'store/update',
  async ({ payload, axiosInstance }, { rejectWithValue }) => {
    try {
      console.log('updateStore: Updating store with data:', payload);
      const axiosToUse = axiosInstance || axios;
      const res = await axiosToUse.put('/merchants/store', payload);
      console.log('updateStore: API response:', res.data);
      return res.data;
    } catch (e) {
      console.error('updateStore: API error:', e.response?.data || e.message);
      return rejectWithValue(e.response?.data || { message: e.message });
    }
  }
);

const initialState = {
  current: null,
  products: [],
  campaigns: [],
  status: 'idle',
  error: null,
};

const storeSlice = createSlice({
  name: 'store',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createOrUpdateStore.pending, (state) => {
        state.status = state.current ? 'succeeded' : 'loading';
      })
      .addCase(createOrUpdateStore.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.current = action.payload;
      })
      .addCase(createOrUpdateStore.rejected, (state, action) => { state.status = 'failed'; state.error = action.error.message; })
      .addCase(fetchStoreBySlug.pending, (state) => { state.status = 'loading'; })
      .addCase(fetchStoreBySlug.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.current = action.payload.store;
        state.products = action.payload.products;
        state.campaigns = action.payload.campaigns || [];
      })
      .addCase(fetchStoreBySlug.rejected, (state, action) => {
        // If redirecting to new slug, keep loading state to prevent "not found" flash
        if (action.payload?.redirecting) {
          state.status = 'loading';
          return;
        }
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchStoreByDomain.pending, (state) => { state.status = 'loading'; })
      .addCase(fetchStoreByDomain.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.current = action.payload.store;
        state.products = action.payload.products;
        state.campaigns = action.payload.campaigns || [];
      })
      .addCase(fetchStoreByDomain.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      });

    builder
      .addCase(fetchMyStore.pending, (state) => {
        state.status = state.current ? 'succeeded' : 'loading';
      })
      .addCase(fetchMyStore.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.current = action.payload;
      })
      .addCase(fetchMyStore.rejected, (state, action) => {
        if (action.payload && action.payload.status === 404) {
          // no store created yet
          state.status = 'idle';
          state.current = null;
          state.error = null;
        } else {
          state.status = 'failed';
          state.error = action.payload?.message || action.error.message;
        }
      });

    builder
      .addCase(updateStore.pending, (state) => {
        state.status = state.current ? 'succeeded' : 'loading';
      })
      .addCase(updateStore.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // Backend returns `{ success, store }`. Reducers written before
        // this contract change wrote the whole envelope to `.current`,
        // which meant reads of e.g. `store.settings` returned undefined
        // right after an update — the state only healed on next
        // `fetchMyStore`. Extract `.store` when present so Redux always
        // holds the actual Store record.
        state.current = action.payload?.store || action.payload;
        state.error = null;
      })
      .addCase(updateStore.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload?.error || action.payload?.message || action.error.message;
      });
  }
});

export default storeSlice.reducer;
